import React, { useState } from 'react';

interface SingularityButtonProps {
  onClick: () => void;
  size?: number;
}

const SingularityButton: React.FC<SingularityButtonProps> = ({ onClick, size = 72 }) => {
  const [ripples, setRipples] = useState<number[]>([]);

  const handleClick = () => {
    const id = Date.now();
    setRipples([...ripples, id]);
    setTimeout(() => {
      setRipples(prev => prev.filter(r => r !== id));
    }, 1300);
    onClick();
  };

  return (
    <button
      onClick={handleClick}
      style={{
        width: size,
        height: size,
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        padding: 0,
        position: 'relative',
        transition: 'transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)',
        outline: 'none',
      }}
      onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.18)'}
      onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
      onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.9)'}
      onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1.18)'}
      aria-label="Cosmic Pulse"
    >
      <svg
        viewBox="0 0 100 100"
        style={{
          width: '100%',
          height: '100%',
          overflow: 'visible',
          display: 'block',
          animation: 'mysticRainbow 7s linear infinite',
        }}
      >
        <defs>
          <filter id="m-glow" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="1.4" result="b"/>
            <feMerge>
              <feMergeNode in="b"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          <filter id="m-soft-glow" x="-80%" y="-80%" width="260%" height="260%">
            <feGaussianBlur stdDeviation="3" result="b"/>
            <feMerge>
              <feMergeNode in="b"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>

        {/* Outer glow layers */}
        <circle cx="50" cy="50" r="45" fill="#a78bfa" opacity="0.06" filter="url(#m-soft-glow)">
          <animate attributeName="r" values="42;52;42" dur="4.5s" repeatCount="indefinite"/>
          <animate attributeName="opacity" values="0.04;0.09;0.04" dur="4.5s" repeatCount="indefinite"/>
        </circle>
        <circle cx="50" cy="50" r="38" fill="#f97316" opacity="0.08" filter="url(#m-soft-glow)">
          <animate attributeName="r" values="34;42;34" dur="3.8s" repeatCount="indefinite"/>
          <animate attributeName="opacity" values="0.05;0.11;0.05" dur="3.8s" repeatCount="indefinite"/>
        </circle>

        {/* Expanding rings */}
        <circle cx="50" cy="50" r="24" fill="none" stroke="#f59e0b" strokeWidth="0.8" opacity="0.55">
          <animate attributeName="r" values="22;48;22" dur="3.6s" repeatCount="indefinite"/>
          <animate attributeName="opacity" values="0.55;0;0.55" dur="3.6s" repeatCount="indefinite"/>
          <animate attributeName="stroke-width" values="0.9;0.2;0.9" dur="3.6s" repeatCount="indefinite"/>
        </circle>
        <circle cx="50" cy="50" r="24" fill="none" stroke="#c4b5fd" strokeWidth="0.6" opacity="0.4">
          <animate attributeName="r" values="22;44;22" dur="3.6s" begin="1.8s" repeatCount="indefinite"/>
          <animate attributeName="opacity" values="0.4;0;0.4" dur="3.6s" begin="1.8s" repeatCount="indefinite"/>
        </circle>

        {/* Rotating dashed orbits */}
        <circle cx="50" cy="50" r="42" fill="none" stroke="#a78bfa" strokeWidth="0.8" opacity="0.5" strokeDasharray="3 9" strokeLinecap="round">
          <animateTransform attributeName="transform" type="rotate" from="0 50 50" to="-360 50 50" dur="14s" repeatCount="indefinite"/>
          <animate attributeName="opacity" values="0.3;0.75;0.3" dur="4s" repeatCount="indefinite"/>
        </circle>
        <circle cx="50" cy="50" r="35" fill="none" stroke="#c4b5fd" strokeWidth="0.9" opacity="0.55" strokeDasharray="5 7" strokeLinecap="round">
          <animateTransform attributeName="transform" type="rotate" from="360 50 50" to="0 50 50" dur="11s" repeatCount="indefinite"/>
          <animate attributeName="opacity" values="0.4;0.8;0.4" dur="3.4s" repeatCount="indefinite"/>
        </circle>

        {/* Twinkling stars */}
        <circle cx="65" cy="28" r="0.7" fill="#ffffff">
          <animate attributeName="opacity" values="0;1;0" dur="2.4s" begin="0.1s" repeatCount="indefinite"/>
        </circle>
        <circle cx="28" cy="72" r="0.6" fill="#ffffff">
          <animate attributeName="opacity" values="0;1;0" dur="2.9s" begin="1.1s" repeatCount="indefinite"/>
        </circle>
        <circle cx="74" cy="68" r="0.8" fill="#ffffff">
          <animate attributeName="opacity" values="0;1;0" dur="2.6s" begin="1.7s" repeatCount="indefinite"/>
        </circle>
        <circle cx="26" cy="30" r="0.5" fill="#ffffff">
          <animate attributeName="opacity" values="0;1;0" dur="2.2s" begin="0.5s" repeatCount="indefinite"/>
        </circle>
        <circle cx="82" cy="50" r="0.55" fill="#ffffff">
          <animate attributeName="opacity" values="0;1;0" dur="3.1s" begin="2s" repeatCount="indefinite"/>
        </circle>
        <circle cx="18" cy="50" r="0.5" fill="#ffffff">
          <animate attributeName="opacity" values="0;1;0" dur="2.7s" begin="0.9s" repeatCount="indefinite"/>
        </circle>

        {/* Orbiting particles */}
        <g>
          <circle cx="78" cy="50" r="1.3" fill="#e9d5ff"/>
          <animateTransform attributeName="transform" type="rotate" from="0 50 50" to="360 50 50" dur="3.2s" repeatCount="indefinite"/>
        </g>
        <g>
          <circle cx="50" cy="24" r="0.9" fill="#fef3c7"/>
          <animateTransform attributeName="transform" type="rotate" from="0 50 50" to="360 50 50" dur="4.8s" repeatCount="indefinite"/>
        </g>
        <g>
          <circle cx="30" cy="50" r="0.75" fill="#fca5a5"/>
          <animateTransform attributeName="transform" type="rotate" from="360 50 50" to="0 50 50" dur="2.8s" repeatCount="indefinite"/>
        </g>

        {/* Core layers */}
        <circle cx="50" cy="50" r="28" fill="#f59e0b" opacity="0.05"/>
        <circle cx="50" cy="50" r="24" fill="#f97316" opacity="0.10"/>
        <circle cx="50" cy="50" r="20" fill="#f97316" opacity="0.09"/>

        {/* Rotating core rings */}
        <circle cx="50" cy="50" r="22" fill="none" stroke="#f97316" strokeWidth="2.4" opacity="0.78" strokeDasharray="7 4" strokeLinecap="round" filter="url(#m-glow)">
          <animateTransform attributeName="transform" type="rotate" from="0 50 50" to="360 50 50" dur="5.5s" repeatCount="indefinite"/>
        </circle>
        <circle cx="50" cy="50" r="17.5" fill="none" stroke="#fde047" strokeWidth="1.8" opacity="0.9" strokeDasharray="4 3" strokeLinecap="round" filter="url(#m-glow)">
          <animateTransform attributeName="transform" type="rotate" from="360 50 50" to="0 50 50" dur="4s" repeatCount="indefinite"/>
        </circle>

        {/* Pulsing rim */}
        <circle cx="50" cy="50" r="12.5" fill="none" stroke="#fffbeb" strokeWidth="1.3" filter="url(#m-glow)">
          <animate attributeName="opacity" values="0.75;1;0.75" dur="2.4s" repeatCount="indefinite"/>
          <animate attributeName="stroke-width" values="1;1.9;1" dur="2.4s" repeatCount="indefinite"/>
        </circle>

        {/* Black hole singularity */}
        <circle cx="50" cy="50" r="9.5" fill="#000000">
          <animate attributeName="r" values="9;10.2;9" dur="2.4s" repeatCount="indefinite"/>
        </circle>
      </svg>

      {/* Ripple effects */}
      {ripples.map(id => (
        <span
          key={id}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: '30px',
            height: '30px',
            marginLeft: '-15px',
            marginTop: '-15px',
            borderRadius: '50%',
            border: '1.5px solid #c4b5fd',
            pointerEvents: 'none',
            animation: 'mysticRipple 1.3s ease-out forwards',
          }}
        />
      ))}

      <style>
        {`
          @keyframes mysticRainbow {
            0% { filter: hue-rotate(0deg) saturate(1.15); }
            100% { filter: hue-rotate(360deg) saturate(1.15); }
          }
          @keyframes mysticRipple {
            to {
              width: 240px;
              height: 240px;
              margin-left: -120px;
              margin-top: -120px;
              border-color: rgba(196,181,253,0);
              border-width: 0.3px;
            }
          }
        `}
      </style>
    </button>
  );
};

export default SingularityButton;
